import { PosPaymentButtons } from "./pages/settings/PosPaymentButtons";
import { PostingSettings } from "./pages/settings/Posting";
import { PriceControls } from "./pages/settings/PriceControls";
import { ChartTemplate } from "./pages/settings/ChartTemplate";
import type { ReactNode } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useSession } from "./lib/session";
import { useLang } from "./i18n";
import { Login } from "./pages/Login";
import { DemoBanner } from "./components/Demo";
import { isExcludedRoute } from "@delta/shared";
import { AccessDenied, NotBuilt, NotInDelta } from "./pages/Simple";
import { Home } from "./pages/Home";
import { Users } from "./pages/Users";
import { Permissions } from "./pages/Permissions";
import { OrganizationSettings } from "./pages/settings/Organization";
import { PeriodLockSettings } from "./pages/settings/PeriodLock";
import { Branches } from "./pages/settings/Branches";
import { BarcodeSettings } from "./pages/settings/BarcodeSettings";
import { Warehouses } from "./pages/settings/Warehouses";
import { Treasuries } from "./pages/settings/Treasuries";
import { Categories, Units } from "./pages/items/NameLists";
import { Items } from "./pages/items/Items";
import { ItemEditor } from "./pages/items/ItemEditor";
import { ItemShow } from "./pages/items/ItemShow";
import { AccountShow, Accounts, AccountTypes, ChartTree } from "./pages/accounting/Chart";
import { CostCenters, ItemKinds, TaxTypes } from "./pages/accounting/Config";
import { CustomerDetail, Customers, SupplierDetail, Suppliers } from "./pages/parties/Parties";
import { JournalEditor, JournalShow, Journals } from "./pages/ledger/Journals";
import { LegacyHistory, LegacyHistoryShow } from "./pages/ledger/LegacyHistory";
import { CashTransfers, MoneyVouchers, OpeningBalances, VoucherShow } from "./pages/vouchers/Vouchers";
import { CountCreate, DepreciationRedirect, StockDocs, StockOnHand, TransferRedirect } from "./pages/stock/Stock";
import { DocEdit, DocShow, InvoiceCreate, InvoiceList, RefundCreate } from "./pages/invoices/Invoices";
import { AccountMovements, AccountStatementReport, PartyBalancesReport, PartyStatementReport, ReportsHub, TreasuryBalancesReport, TreasuryStatementReport } from "./pages/ledger/Reports";
import {
  BalanceSheetReport, ChartOfAccountsReport, IncomeStatementReport, InvoiceDetailsReport, InvoiceProfitsReport, InvoiceRegisterReport, InvoiceTaxReport, MoneyReport, MovementSummaryReport, ItemMovementReport,
  TaxReturnReport, TobaccoFeesReport, TransfersReport, WarehouseBalancesReport, WarehouseItemsReport, AssetsReport, AssetDepreciationReport, AssetInvoicesReport, AssetInvoiceDetailsReport
} from "./pages/ledger/DocReports";
import { CashFlowIndicatorsReport, CashFlowMappingsPage, CashFlowReport } from "./pages/ledger/FinanceReports";
import { AllAssets, AssetKinds, Depreciation } from "./pages/assets/Assets";
import { AssetPurchases, AssetPurchaseShow, NewAssetPurchase } from "./pages/assets/Purchases";
import { Printers } from "./pages/settings/Printers";
import { Partners } from "./pages/settings/Partners";
import { ExpenseTypes } from "./pages/settings/ExpenseTypes";
import { TobaccoSettings } from "./pages/settings/Tobacco";
import { ClientArea } from "./pages/settings/ClientArea";
import { PackageSettings } from "./pages/settings/Package";
import { PackageClosed } from "./pages/PackageClosed";
import { BarcodePrint } from "./pages/items/BarcodePrint";
import { InvoicePrint } from "./pages/invoices/InvoicePrint";
import { PosHome, PosSale } from "./pages/pos/Pos";
import { PosInvoices, PosRefund, PosReports } from "./pages/pos/PosDocs";
import { ShiftReportPage } from "./pages/pos/ShiftReport";
import { ItemImport } from "./pages/items/ItemImport";
import { PosPrinters, SettingsPrintersThisPc } from "./pages/pos/PosPrinters";
import { TrainingRegister } from "./pages/pos/TrainingRegister";
import { OfflineRegister } from "./pages/pos/OfflineRegister";
import { PosRegisters } from "./pages/pos/PosRegisters";
import { Signup } from "./pages/Signup";
import { SignupStatus } from "./pages/SignupStatus";
import { Recover } from "./pages/Recover";
import { OwnerSetup } from "./pages/OwnerSetup";
import { Onboarding } from "./pages/Onboarding";
import { PlatformApp } from "./pages/platform/Platform";
import { ResellerApp } from "./pages/reseller/Reseller";

/** Signed-in screens: sends guests to the login page and shows Access denied when keys are missing. */
function Guard({ children, anyUser }: { children: ReactNode; anyUser?: boolean }) {
  const { me, loading, missingFor, closed } = useSession();
  const { t } = useLang();
  const loc = useLocation();
  if (loading) return <div className="state-box"><p>{t("loading")}</p></div>;
  if (!me) return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  // The business's software is closed (migration 044): the owner keeps the package screen; everything else, for
  // everyone, is the closed screen. The server refuses the same requests.
  if (closed && !(me.user.owner && loc.pathname === "/settings/package")) return <PackageClosed />;
  const miss = anyUser ? [] : missingFor(loc.pathname);
  return miss.length ? <AccessDenied missing={miss} /> : <>{children}</>;
}

function Fallback() {
  const { pathname } = useLocation();
  return isExcludedRoute(pathname) ? <Guard anyUser><NotInDelta /></Guard> : <Guard><NotBuilt /></Guard>;
}

export function App() {
  const { me, loading } = useSession();
  // Each path is a fresh screen (acceptance pass, D-5): two routes that share a page component (vouchers of each kind,
  // the trial balances) would otherwise keep the previous screen's dialogs, selections and loaded rows.
  const loc = useLocation();
  // The Delta Platform (Super Admin) portal: its own sign-in and screens, kept mounted while moving between them.
  if (loc.pathname === "/platform" || loc.pathname.startsWith("/platform/")) {
    return <Routes><Route path="/platform/*" element={<PlatformApp />} /></Routes>;
  }
  // The reseller portal (migration 048): its own sign-in and screens too.
  if (loc.pathname === "/reseller" || loc.pathname.startsWith("/reseller/")) {
    return <Routes><Route path="/reseller/*" element={<ResellerApp />} /></Routes>;
  }
  return (<>
    <DemoBanner />
    <Routes location={loc} key={loc.pathname}>
      <Route path="/login" element={!loading && me ? <Navigate to={me.organization.onboardingPending && me.user.administrator ? "/onboarding" : "/home"} replace /> : <Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/signup/status" element={<SignupStatus />} />
      <Route path="/recover" element={<Recover />} />
      <Route path="/owner-setup" element={<OwnerSetup />} />
      <Route path="/onboarding" element={<Guard><Onboarding /></Guard>} />
      <Route path="/" element={<Navigate to="/home" replace />} />
      <Route path="/index" element={<Navigate to="/home" replace />} />
      <Route path="/home" element={<Guard><Home /></Guard>} />
      <Route path="/users-settings" element={<Guard><Users /></Guard>} />
      <Route path="/permission/:id" element={<Guard><Permissions /></Guard>} />
      <Route path="/settings/organization" element={<Guard><OrganizationSettings /></Guard>} />
      <Route path="/settings/period-lock" element={<Guard><PeriodLockSettings /></Guard>} />
      <Route path="/settings/pos-payment-buttons" element={<Guard><PosPaymentButtons /></Guard>} />
      <Route path="/settings/posting" element={<Guard><PostingSettings /></Guard>} />
      <Route path="/settings/chart-template" element={<Guard><ChartTemplate /></Guard>} />
      <Route path="/price-controls" element={<Guard><PriceControls /></Guard>} />
      <Route path="/settings/branches" element={<Guard><Branches /></Guard>} />
      <Route path="/branch-barcode/:storeId" element={<Guard><BarcodeSettings /></Guard>} />
      <Route path="/warehouses/all" element={<Guard><Warehouses /></Guard>} />
      <Route path="/settings/treasury" element={<Guard><Treasuries /></Guard>} />
      <Route path="/settings/accountIndex" element={<Guard><Accounts /></Guard>} />
      <Route path="/settings/accountTypes" element={<Guard><AccountTypes /></Guard>} />
      <Route path="/chart" element={<Guard><ChartTree /></Guard>} />
      <Route path="/account/:id" element={<Guard><AccountShow /></Guard>} />
      <Route path="/cost-centers" element={<Guard><CostCenters /></Guard>} />
      <Route path="/settings/tax" element={<Guard><TaxTypes /></Guard>} />
      <Route path="/settings/expense-types" element={<Guard><ExpenseTypes /></Guard>} />
      <Route path="/settings/tobacco" element={<Guard><TobaccoSettings /></Guard>} />
      <Route path="/item-kinds" element={<Guard><ItemKinds /></Guard>} />
      <Route path="/item-Category" element={<Guard><Categories /></Guard>} />
      <Route path="/unitsConfig" element={<Guard><Units /></Guard>} />
      <Route path="/items/allItems" element={<Guard><Items /></Guard>} />
      <Route path="/items/import" element={<Guard><ItemImport /></Guard>} />
      <Route path="/items/create" element={<Guard><ItemEditor key="new" /></Guard>} />
      <Route path="/items/edit/:id" element={<Guard><ItemEditor /></Guard>} />
      <Route path="/items/show/:id" element={<Guard><ItemShow /></Guard>} />
      <Route path="/Customer" element={<Guard><Customers /></Guard>} />
      <Route path="/Customer/:id" element={<Guard><CustomerDetail /></Guard>} />
      <Route path="/Supplier" element={<Guard><Suppliers /></Guard>} />
      <Route path="/Supplier/:id" element={<Guard><SupplierDetail /></Guard>} />
      <Route path="/journals" element={<Guard><Journals /></Guard>} />
      <Route path="/journals/create" element={<Guard><JournalEditor key="new" /></Guard>} />
      <Route path="/journals/edit/:id" element={<Guard><JournalEditor /></Guard>} />
      <Route path="/journals/show/:id" element={<Guard><JournalShow /></Guard>} />
      <Route path="/legacy-history" element={<Guard><LegacyHistory /></Guard>} />
      <Route path="/legacy-history/:id" element={<Guard><LegacyHistoryShow /></Guard>} />
      <Route path="/Expense" element={<Guard><MoneyVouchers kind="expense" /></Guard>} />
      <Route path="/receipts/Deposit" element={<Guard><MoneyVouchers kind="deposit" /></Guard>} />
      <Route path="/receipts/Withdraw" element={<Guard><MoneyVouchers kind="withdraw" /></Guard>} />
      <Route path="/receipts/Collect" element={<Guard><MoneyVouchers kind="collect" /></Guard>} />
      <Route path="/receipts/Payment" element={<Guard><MoneyVouchers kind="payment" /></Guard>} />
      <Route path="/receipt/:id" element={<Guard><VoucherShow family="money" /></Guard>} />
      <Route path="/transfers" element={<Guard><CashTransfers /></Guard>} />
      <Route path="/transfer/:id" element={<Guard><VoucherShow family="transfer" /></Guard>} />
      <Route path="/opening-balances" element={<Guard><OpeningBalances /></Guard>} />
      <Route path="/opening-balance/:id" element={<Guard><VoucherShow family="opening" /></Guard>} />
      <Route path="/invoices/BeginningInventory" element={<Guard><StockDocs kind="beginning_inventory" /></Guard>} />
      <Route path="/invoices/Corrupted" element={<Guard><StockDocs kind="corrupted" /></Guard>} />
      <Route path="/invoices/Transfer" element={<Guard><StockDocs kind="stock_transfer" /></Guard>} />
      <Route path="/invoices/Inventory" element={<Guard><StockDocs kind="inventory" /></Guard>} />
      <Route path="/invoices/Purchase" element={<Guard><InvoiceList kind="purchase" /></Guard>} />
      <Route path="/invoices/PurchaseRefund" element={<Guard><InvoiceList kind="purchase_refund" /></Guard>} />
      <Route path="/invoices/Sale" element={<Guard><InvoiceList kind="sale" /></Guard>} />
      <Route path="/invoices/SaleRefund" element={<Guard><InvoiceList kind="sale_refund" /></Guard>} />
      <Route path="/invoices/Quotation" element={<Guard><InvoiceList kind="quotation" /></Guard>} />
      <Route path="/invoices/create" element={<Guard><InvoiceCreate /></Guard>} />
      <Route path="/invoices/edit/:id" element={<Guard><DocEdit /></Guard>} />
      <Route path="/invoices/show/:id" element={<Guard><DocShow /></Guard>} />
      <Route path="/invoices/print/:id" element={<Guard><InvoicePrint /></Guard>} />
      <Route path="/refunds/create/:id" element={<Guard><RefundCreate /></Guard>} />
      <Route path="/warehouses/inventory/new" element={<Guard><CountCreate /></Guard>} />
      <Route path="/warehouses/transfers/new" element={<Guard><TransferRedirect /></Guard>} />
      <Route path="/warehouses/depreciation/new" element={<Guard><DepreciationRedirect /></Guard>} />
      <Route path="/warehouse-stock" element={<Guard><StockOnHand /></Guard>} />
      <Route path="/printers" element={<Guard><Printers /></Guard>} />
      <Route path="/printers/this-pc" element={<Guard><SettingsPrintersThisPc /></Guard>} />
      <Route path="/partners" element={<Guard><Partners /></Guard>} />
      <Route path="/client-area" element={<Guard><ClientArea /></Guard>} />
      <Route path="/settings/package" element={<Guard><PackageSettings /></Guard>} />
      <Route path="/items/qrPrint" element={<Guard><BarcodePrint /></Guard>} />
      <Route path="/pos" element={<Guard><PosHome /></Guard>} />
      <Route path="/pos/sale" element={<Guard><PosSale /></Guard>} />
      <Route path="/pos/refund" element={<Guard><PosRefund /></Guard>} />
      <Route path="/pos/invoices" element={<Guard><PosInvoices /></Guard>} />
      <Route path="/pos/reports" element={<Guard><PosReports /></Guard>} />
      <Route path="/pos/shifts/:id" element={<Guard><ShiftReportPage /></Guard>} />
      <Route path="/pos/printers" element={<Guard><PosPrinters /></Guard>} />
      <Route path="/assets/types" element={<Guard><AssetKinds /></Guard>} />
      <Route path="/assets/all" element={<Guard><AllAssets /></Guard>} />
      <Route path="/assets/depreciation" element={<Guard><Depreciation /></Guard>} />
      <Route path="/assets/purchases" element={<Guard><AssetPurchases /></Guard>} />
      <Route path="/assets/new-purchase" element={<Guard><NewAssetPurchase /></Guard>} />
      <Route path="/assets-purchase/show/:id" element={<Guard><AssetPurchaseShow /></Guard>} />
      <Route path="/reports" element={<Guard><ReportsHub /></Guard>} />
      <Route path="/reports/general-ledger" element={<Guard><AccountMovements kind="gl" /></Guard>} />
      <Route path="/reports/balance-trial/:type" element={<Guard><AccountMovements kind="tb" /></Guard>} />
      <Route path="/reports/charts-statement" element={<Guard><AccountStatementReport /></Guard>} />
      <Route path="/reports/account-statement" element={<Guard><PartyStatementReport /></Guard>} />
      <Route path="/reports/accounts" element={<Guard><PartyBalancesReport /></Guard>} />
      <Route path="/reports/journals-report" element={<Guard><TreasuryBalancesReport /></Guard>} />
      <Route path="/reports/journals-statement" element={<Guard><TreasuryStatementReport /></Guard>} />
      <Route path="/reports/chart-of-accounts" element={<Guard><ChartOfAccountsReport /></Guard>} />
      <Route path="/reports/income-statement" element={<Guard><IncomeStatementReport /></Guard>} />
      <Route path="/reports/balance-sheet" element={<Guard><BalanceSheetReport /></Guard>} />
      <Route path="/reports/cash-flow" element={<Guard><CashFlowReport /></Guard>} />
      <Route path="/reports/cash-flow/classification" element={<Guard><CashFlowMappingsPage /></Guard>} />
      <Route path="/reports/cash-flow-indicators" element={<Guard><CashFlowIndicatorsReport /></Guard>} />
      <Route path="/reports/receipt-payment" element={<Guard><MoneyReport all /></Guard>} />
      <Route path="/reports/all-invoices/:kind" element={<Guard><InvoiceRegisterReport /></Guard>} />
      <Route path="/reports/invoices-details" element={<Guard><InvoiceDetailsReport /></Guard>} />
      <Route path="/reports/invoices-profits" element={<Guard><InvoiceProfitsReport /></Guard>} />
      <Route path="/reports/money/:kind" element={<Guard><MoneyReport /></Guard>} />
      <Route path="/reports/transfers" element={<Guard><TransfersReport /></Guard>} />
      <Route path="/reports/item-movement" element={<Guard><ItemMovementReport /></Guard>} />
      <Route path="/reports/movement-summary" element={<Guard><MovementSummaryReport /></Guard>} />
      <Route path="/reports/tax/:kind" element={<Guard><InvoiceTaxReport /></Guard>} />
      <Route path="/reports/tax-return" element={<Guard><TaxReturnReport /></Guard>} />
      <Route path="/reports/tobacco-fees" element={<Guard><TobaccoFeesReport /></Guard>} />
      <Route path="/reports/asset-tax" element={<Guard><AssetInvoicesReport tax /></Guard>} />
      <Route path="/reports/all-assets" element={<Guard><AssetsReport /></Guard>} />
      <Route path="/reports/asset-depreciation" element={<Guard><AssetDepreciationReport /></Guard>} />
      <Route path="/reports/asset-invoices" element={<Guard><AssetInvoicesReport /></Guard>} />
      <Route path="/reports/asset-invoice-details" element={<Guard><AssetInvoiceDetailsReport /></Guard>} />
      <Route path="/reports/warehouse" element={<Guard><WarehouseBalancesReport /></Guard>} />
      <Route path="/reports/warehouses-items" element={<Guard><WarehouseItemsReport /></Guard>} />
      {/* The training register runs on the register PC against the local POS agent; the cashier signs in through the
          agent (an offline grant), not with a back-office session. Training mode only. */}
      <Route path="/pos/training" element={<TrainingRegister />} />
      {/* The offline register (docs/offline-pos.md) runs inside Delta POS for Windows from the copy of this web app kept
          on the PC, without Delta: no session, no API. The app's main process holds the sales and the authorization. */}
      <Route path="/pos/offline" element={<OfflineRegister />} />
      <Route path="/pos/registers" element={<Guard anyUser><PosRegisters /></Guard>} />
      {/* Every other audited route keeps its place until its module is built; HR routes say they are not part of Delta. */}
      <Route path="*" element={<Fallback />} />
    </Routes>
  </>);
}
